import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, fmtMoney, CURRENCY_META } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Plus, Wallet, TrendingUp, ArrowLeftRight, X, Loader2, Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";


function CreateAccountModal({ currencies, existing, onClose, onCreated }) {
  const { t, lang } = useI18n();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const available = currencies.filter((c) => c.is_active && !existing.includes(c.code));

  const create = async () => {
    if (!code) return;
    setBusy(true);
    try {
      await api.post("/accounts", { currency_code: code });
      toast.success(`${t("dash.created")} ${code}`);
      onCreated(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md ob-card rounded-md p-6" onClick={(e) => e.stopPropagation()} data-testid="create-account-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-bold text-white">{t("dash.openNew")}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={20} /></button>
        </div>
        {available.length === 0 ? (
          <p className="text-zinc-500 text-sm">{t("dash.allCurrencies")}</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mb-6">
              {available.map((c) => (
                <button key={c.code} onClick={() => setCode(c.code)} data-testid={`pick-currency-${c.code}`}
                  className={`text-start p-4 rounded-md border transition-all ${code === c.code ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-zinc-800 hover:border-zinc-700"}`}>
                  <div className="text-2xl mb-1">{CURRENCY_META[c.code]?.flag || "🏦"}</div>
                  <div className="font-mono-ob text-[#00E5FF] text-sm">{c.code}</div>
                  <div className="text-xs text-zinc-400">{lang === "ar" ? c.name_ar : c.name}</div>
                </button>
              ))}
            </div>
            <button onClick={create} disabled={!code || busy} data-testid="confirm-create-account"
              className="w-full bg-[#00E5FF] text-black font-bold py-3 rounded-md hover:bg-[#33EBFF] transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
              {busy && <Loader2 size={18} className="animate-spin" />} {t("dash.openAccount")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function AccountCard({ acc, currency, onChanged }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const dec = currency?.decimals ?? 2;

  const setDefault = async (e) => {
    e.stopPropagation();
    try { await api.post(`/accounts/${acc.id}/set-default`); toast.success(t("dash.setDefault")); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div onClick={() => navigate(`/accounts/${acc.id}`)} data-testid={`account-card-${acc.currency_code}`}
      className="ob-card rounded-md p-5 cursor-pointer relative group">
      {acc.is_default && (
        <span className="absolute top-4 start-4 text-[10px] font-bold text-[#00E5FF] bg-[#00E5FF]/10 px-2 py-0.5 rounded-full flex items-center gap-1">
          <Star size={10} fill="#00E5FF" /> {t("dash.default")}
        </span>
      )}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{CURRENCY_META[acc.currency_code]?.flag || "🏦"}</span>
          <div>
            <div className="font-mono-ob text-[#00E5FF] font-semibold">{acc.currency_code}</div>
            <div className="text-xs text-zinc-500">{lang === "ar" ? currency?.name_ar : currency?.name}</div>
          </div>
        </div>
        {!acc.is_default && (
          <button onClick={setDefault} data-testid={`set-default-${acc.currency_code}`}
            className="text-zinc-600 hover:text-[#00E5FF] transition-colors"><Star size={16} /></button>
        )}
      </div>
      <div className="mb-4">
        <div className="text-xs text-zinc-500 mb-1">{t("dash.available")}</div>
        <div className="font-num text-2xl font-bold text-white" dir="ltr" data-testid={`balance-${acc.currency_code}`}>
          {fmtMoney(acc.available_balance, dec)} <span className="text-sm text-zinc-500">{currency?.symbol}</span>
        </div>
        {acc.pending_balance > 0 && (
          <div className="text-xs text-amber-400 mt-1" dir="ltr">{t("dash.pending")}: {fmtMoney(acc.pending_balance, dec)}</div>
        )}
      </div>
      <div className="pt-3 border-t border-zinc-900 space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-zinc-500">{t("dash.accountNo")}</span>
          <span className="font-mono-ob text-zinc-300" dir="ltr">{acc.account_number}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-zinc-500">IBAN</span>
          <span className="font-mono-ob text-zinc-400" dir="ltr">{acc.iban}</span>
        </div>
      </div>
      <div className="glow-line mt-4 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth()
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [summary, setSummary] = useState(null);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    try {
      const [a, c, s, r] = await Promise.all([
        api.get("/accounts"), api.get("/currencies"), api.get("/summary"), api.get("/rates"),
      ]);
      setAccounts(a.data); setCurrencies(c.data); setSummary(s.data); setRates(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (!user) return;

    if (user.role !== "user") {
      navigate("/login");
      return;
    }

    load();
  }, [user]);

  const curMap = Object.fromEntries(currencies.map((c) => [c.code, c]));
  const openAccounts = accounts.filter((a) => a.status !== "closed");
  const baseSym = curMap[summary?.base_currency]?.symbol || "";

  if (loading) return <div className="w-48 ob-loader h-1 rounded-full bg-zinc-900 mx-auto mt-20" />;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">{t("dash.title")}</h1>
          <p className="text-zinc-500 text-sm mt-1">{t("dash.sub")}</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="open-create-account"
          className="bg-[#00E5FF] text-black font-bold px-5 py-2.5 rounded-md hover:bg-[#33EBFF] transition-colors flex items-center gap-2">
          <Plus size={18} /> {t("dash.newAccount")}
        </button>
      </div>

      <div className="relative overflow-hidden ob-card rounded-md p-8">
        <img src="https://images.pexels.com/photos/6732010/pexels-photo-6732010.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-xs font-semibold tracking-widest text-zinc-400 uppercase">
            <Wallet size={14} /> {t("dash.totalBalance")}
          </div>
          <div className="mt-3 font-num text-5xl font-extrabold text-white" dir="ltr" data-testid="total-balance-value">
            {fmtMoney(summary?.total_in_base, 2)} <span className="text-2xl text-[#00E5FF]">{baseSym} {summary?.base_currency}</span>
          </div>
          <div className="text-sm text-zinc-500 mt-2">{summary?.accounts_count} {t("dash.activeAccounts")}</div>
          <div className="glow-line mt-5 w-40" />
        </div>
      </div>

      {rates.length > 0 && (
        <div className="ob-card rounded-md px-5 py-3 flex items-center gap-6 overflow-x-auto" data-testid="rate-ticker">
          <span className="flex items-center gap-1 text-xs text-zinc-500 shrink-0"><TrendingUp size={14} /> {t("dash.rates")}</span>
          {rates.map((r) => (
            <span key={`${r.from_currency}-${r.to_currency}`} className="font-mono-ob text-sm whitespace-nowrap" dir="ltr">
              <span className="text-zinc-400">{r.from_currency}/{r.to_currency}</span> <span className="text-[#00E5FF]">{r.internal_rate}</span>
            </span>
          ))}
        </div>
      )}

      {openAccounts.length === 0 ? (
        <div className="ob-card rounded-md p-12 text-center">
          <Wallet size={40} className="mx-auto text-zinc-700 mb-4" />
          <p className="text-zinc-400 mb-4">{t("dash.empty")}</p>
          <button onClick={() => setShowCreate(true)} className="bg-[#00E5FF] text-black font-bold px-5 py-2.5 rounded-md hover:bg-[#33EBFF] inline-flex items-center gap-2">
            <Plus size={18} /> {t("dash.openAccount")}
          </button>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">{t("dash.myAccounts")} ({openAccounts.length})</h2>
            <button onClick={() => navigate("/transfer")} className="text-sm text-[#00E5FF] hover:underline flex items-center gap-1">
              <ArrowLeftRight size={14} /> {t("dash.transferBetween")}
            </button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {openAccounts.map((acc) => (
              <AccountCard key={acc.id} acc={acc} currency={curMap[acc.currency_code]} onChanged={load} />
            ))}
          </div>
        </div>
      )}

      {showCreate && (
        <CreateAccountModal currencies={currencies} existing={openAccounts.map((a) => a.currency_code)}
          onClose={() => setShowCreate(false)} onCreated={load} />
      )}
    </div>
  );
}
